/* Mock routes — NGO and government campaigns. */

import type { Campaign, Governorate } from '@core/types'
import { ApiError } from '../../http'
import { db, nextId } from '../store'
import { readNumber, readString, type MockRoute } from '../types'

export interface CampaignSummary {
  campaigns: number
  activeCampaigns: number
  targetTrees: number
  plantedTrees: number
  verifiedTrees: number
  survivingTrees: number
  volunteers: number
  survivalRate: number
  verificationRate: number
}

export const campaignRoutes: MockRoute[] = [
  {
    method: 'GET',
    pattern: '/campaigns/summary',
    handler: () => summarise(db.campaigns),
    emptyState: {
      campaigns: 0,
      activeCampaigns: 0,
      targetTrees: 0,
      plantedTrees: 0,
      verifiedTrees: 0,
      survivingTrees: 0,
      volunteers: 0,
      survivalRate: 0,
      verificationRate: 0,
    } satisfies CampaignSummary,
  },
  {
    method: 'GET',
    pattern: '/campaigns',
    handler: ({ query }) => {
      const status = query.get('status')
      const governorate = query.get('governorate')
      const search = query.get('search')?.toLowerCase()

      let campaigns = [...db.campaigns]
      if (status && status !== 'all') campaigns = campaigns.filter((item) => item.status === status)
      if (governorate && governorate !== 'all') {
        campaigns = campaigns.filter((item) => item.governorate === governorate)
      }
      if (search) {
        campaigns = campaigns.filter(
          (item) =>
            item.name.toLowerCase().includes(search) ||
            item.organisation.toLowerCase().includes(search),
        )
      }
      return campaigns.sort(
        (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime(),
      )
    },
    emptyState: [] satisfies Campaign[],
  },
  {
    method: 'GET',
    pattern: '/campaigns/:id',
    handler: ({ params }) => {
      const campaign = db.campaigns.find((item) => item.id === params.id)
      if (!campaign) {
        throw new ApiError({
          status: 404,
          code: 'campaign_not_found',
          message: 'That campaign is not available. It may belong to another organisation.',
        })
      }
      return campaign
    },
  },
  {
    method: 'POST',
    pattern: '/campaigns',
    handler: ({ body }) => {
      const fieldErrors: Record<string, string> = {}
      const name = readString(body, 'name')
      const organisation = readString(body, 'organisation')
      const governorate = readString(body, 'governorate')
      const district = readString(body, 'district')
      const targetTrees = readNumber(body, 'targetTrees')
      const startsAt = readString(body, 'startsAt')
      const endsAt = readString(body, 'endsAt')

      if (!name) fieldErrors.name = 'Give the campaign a name the volunteers will recognise.'
      if (!organisation) fieldErrors.organisation = 'State the organisation running the campaign.'
      if (!governorate) fieldErrors.governorate = 'Select the governorate.'
      if (!district) fieldErrors.district = 'Add the district or site name.'
      if (!targetTrees) fieldErrors.targetTrees = 'Set a target number of trees.'
      if (!startsAt) fieldErrors.startsAt = 'Set a start date.'
      if (!endsAt) fieldErrors.endsAt = 'Set an end date.'
      if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) {
        fieldErrors.endsAt = 'The end date must be after the start date.'
      }
      if (Object.keys(fieldErrors).length > 0) {
        throw new ApiError({
          status: 422,
          code: 'validation_failed',
          message: 'The campaign could not be created yet — some details need attention.',
          fieldErrors,
        })
      }

      const campaign: Campaign = {
        id: nextId('cp', 100),
        name: name as string,
        organisation: organisation as string,
        governorate: governorate as Governorate,
        district: district as string,
        startsAt: new Date(startsAt as string).toISOString(),
        endsAt: new Date(endsAt as string).toISOString(),
        targetTrees: targetTrees as number,
        plantedTrees: 0,
        verifiedTrees: 0,
        survivingTrees: 0,
        volunteers: 0,
        status: 'planned',
        speciesSummary: readString(body, 'speciesSummary') ?? 'Species to be confirmed',
      }

      db.campaigns.unshift(campaign)
      return campaign
    },
  },
]

function summarise(campaigns: Campaign[]): CampaignSummary {
  const totals = campaigns.reduce(
    (accumulator, campaign) => ({
      targetTrees: accumulator.targetTrees + campaign.targetTrees,
      plantedTrees: accumulator.plantedTrees + campaign.plantedTrees,
      verifiedTrees: accumulator.verifiedTrees + campaign.verifiedTrees,
      survivingTrees: accumulator.survivingTrees + campaign.survivingTrees,
      volunteers: accumulator.volunteers + campaign.volunteers,
    }),
    { targetTrees: 0, plantedTrees: 0, verifiedTrees: 0, survivingTrees: 0, volunteers: 0 },
  )

  return {
    campaigns: campaigns.length,
    activeCampaigns: campaigns.filter((campaign) => campaign.status === 'active').length,
    ...totals,
    survivalRate: totals.plantedTrees === 0 ? 0 : totals.survivingTrees / totals.plantedTrees,
    verificationRate: totals.plantedTrees === 0 ? 0 : totals.verifiedTrees / totals.plantedTrees,
  }
}
